import { Navigate, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { AppShell } from "@marekl/adminer/shell";
import { AdminerToaster } from "@marekl/adminer/components";
import { useAuth } from "../../auth/useAuth";
import { canViewCompetition, readsCompetitionOnly, useLogout } from "../../auth/queries";
import { useCompetition } from "../../lib/queries/competitions";
import { ImpersonationBanner } from "../../components/ImpersonationBanner";
import { SeasonProvider } from "../../season";
import { CompetitionSidebar } from "./CompetitionSidebar";
import { SHELL_CLASS } from "../shellMarker";
import { CompetitionReadOnlyContext } from "./readOnly";

// Still its own component, but only to publish the outlet context: closing the
// drawer on navigation is AppShell's job now, via navigationKey.
function MainOutlet() {
  const location = useLocation();
  return <Outlet context={{ currentPath: location.pathname }} />;
}

// CompetitionLayout is the „Režim Soutěž" shell at /soutez/:id/*. It is
// a sibling of AppLayout: same AppShell chrome, but the sidebar navigates
// one competition. No page here shows the season switcher — the
// competition pins its own season — which is simply the AppTopbar default;
// see the `seasonSwitcher` prop there. SeasonProvider still mounts so
// shared components relying on useSeason keep working.
//
// Access: rbac.CanViewCompetition — whoever manages it (admin /
// competitions_admin, or the competition's organizer), and the read-only
// competitions_viewer, for whom the pages switch their writes off through
// CompetitionReadOnlyContext. The backend enforces both regardless.
export function CompetitionLayout() {
  const { user } = useAuth();
  const { id } = useParams();
  const { mutate: logout } = useLogout();
  const navigate = useNavigate();
  const location = useLocation();
  const competitionId = id ?? "";
  const { data: competition } = useCompetition(
    competitionId || undefined,
  );

  if (!user) return null;
  if (!competitionId || !canViewCompetition(user, competitionId)) {
    return <Navigate to="/dashboard" replace />;
  }

  function handleLogout() {
    logout(undefined, { onSettled: () => navigate("/login", { replace: true }) });
  }

  return (
    <SeasonProvider>
      <AppShell
        className={SHELL_CLASS}
        sidebar={
          <CompetitionSidebar
            competitionId={competitionId}
            competition={competition}
            user={user}
            onLogout={handleLogout}
          />
        }
        navigationKey={location.pathname}
        toaster={<AdminerToaster position="bottom-right" toastOptions={{ duration: 4000 }} />}
      >
        {user.impersonator && <ImpersonationBanner user={user} />}
        <CompetitionReadOnlyContext.Provider value={readsCompetitionOnly(user, competitionId)}>
          <MainOutlet />
        </CompetitionReadOnlyContext.Provider>
      </AppShell>
    </SeasonProvider>
  );
}
